import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { toAnchored, toWorld, type BoardImage } from "@board/shared";
import { useBoard } from "./store";
import type { InkRegion } from "./InkLayer";
import type { Transform } from "./useCanvasTransform";

const ACCEPT = ".png,.jpg,.jpeg,.webp,.gif,.pdf,.txt,.csv,.docx,.xlsx,.pptx";
const MAX_BYTES = 20 * 1024 * 1024;
const DRAG_THRESHOLD = 5;

function asDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.readAsDataURL(file);
  });
}

async function dimensionsOf(file: File) {
  if (!file.type.startsWith("image/")) return { width: 320, height: 180 };
  const bitmap = await createImageBitmap(file);
  try { return { width: bitmap.width, height: bitmap.height }; }
  finally { bitmap.close(); }
}

type Gesture = { pointer: number; id: string; mode: "move" | "resize"; x: number; y: number;
  canvasX: number; canvasY: number; clientId: string | null;
  scale: number; boxWidth: number; boxHeight: number; moved: boolean };

/** How far above the paperclip a file from the rail lands, in screen pixels. */
const ABOVE_THE_CLIP = 90;

export function AttachmentLayer({ transform, screenToWorld, regions, focusedClientId,
  attachPoint, onAttachHandled }: {
  transform: Transform;
  screenToWorld: (x: number, y: number) => { x: number; y: number };
  regions: InkRegion[];
  focusedClientId: string | null;
  /** A board point to attach at, set by the right-click menu. */
  attachPoint: { x: number; y: number } | null;
  /** Called once that point has been acted on, so it fires exactly once. */
  onAttachHandled: () => void;
}) {
  const tool = useBoard((state) => state.tool);
  const setTool = useBoard((state) => state.setTool);
  const selectedClientId = useBoard((state) => state.selectedClientId);
  const clients = useBoard((state) => state.clients);
  const me = useBoard((state) => state.me);
  const images = useBoard((state) => state.images);
  const selected = useBoard((state) => state.selectedObject);
  const selectObject = useBoard((state) => state.selectObject);
  const create = useBoard((state) => state.createAttachment);
  const update = useBoard((state) => state.transformAttachment);
  const remove = useBoard((state) => state.deleteAttachment);
  const input = useRef<HTMLInputElement>(null);
  const placement = useRef<{ clientId: string | null; x: number; y: number } | null>(null);
  const gesture = useRef<Gesture | null>(null);
  const [preview, setPreview] = useState<{ id: string; x: number; y: number; scale: number } | null>(null);
  const [problem, setProblem] = useState("");
  const [busy, setBusy] = useState(false);

  // In a client's own board that client is the target; on the full board it is
  // whichever column was last clicked.
  const targetClientId = focusedClientId ?? selectedClientId;

  // The message used to be cleared whenever the paperclip went back on the
  // rail. That worked while the tool stayed armed waiting for a click; now it
  // is put down in the same breath it is picked up, so that rule wiped every
  // message the moment it was written. What the message is really about is the
  // situation it was raised in, so it survives until that changes.
  useEffect(() => { setProblem(""); }, [targetClientId]);

  // Attaching from the board's right-click menu: the point that was clicked.
  useEffect(() => {
    if (!attachPoint) return;
    attachAt(attachPoint);
    onAttachHandled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachPoint]);

  // Picking the paperclip up is the whole gesture: the dialog opens on the next
  // frame and the tool goes straight back on the rail, because there is nothing
  // left for the mode to wait for.
  //
  // The file lands just above the paperclip. That is the nearest clear space to
  // the hand that reached for it — you are looking at the rail, so the file
  // appears where you are looking rather than somewhere the board decided.
  useEffect(() => {
    if (tool !== "attach" || busy) return;
    const clip = document.querySelector<HTMLElement>('[aria-label="Attach a file"]');
    const box = clip?.getBoundingClientRect();
    if (box) {
      attachAt(screenToWorld(box.left + box.width / 2, box.top - ABOVE_THE_CLIP));
    }
    setTool("select");
    // Runs on taking the tool, not on every change to the board underneath it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tool]);

  const displayed = useMemo(() => images
    .filter((item) => !focusedClientId || item.client_id === focusedClientId)
    .map((item) => {
      const anchor = regions.find((candidate) => candidate.clientId === item.client_id) ?? null;
      const at = toWorld(anchor, { x: item.canvas_x, y: item.canvas_y });
      return { item, x: at.x, y: at.y };
    }), [focusedClientId, images, regions]);

  /**
   * A file goes where you asked for it, and nowhere else.
   *
   * Two ways in, and both name a point on the board before the dialog opens:
   * the board's right-click menu attaches at the point you right-clicked, and
   * the paperclip on the rail attaches just above itself, which is the nearest
   * clear space to the hand that reached for it. Whichever column contains that
   * point owns the file; a point outside every column makes it a loose board
   * attachment, standing where it was put.
   *
   * This replaced an automatic slot to the right of the column. A rule that
   * decides for you only beats a point you chose while you cannot choose one —
   * and now you always can.
   */
  function attachAt(world: { x: number; y: number }) {
    const region = regionAt(world);
    const at = toAnchored(region, world);
    placement.current = { clientId: region?.clientId ?? null, x: at.x, y: at.y };
    setProblem("");
    input.current?.click();
  }

  /** The column under a world point, if any. */
  function regionAt(point: { x: number; y: number }) {
    return regions.find((candidate) => point.x >= candidate.x
      && point.x <= candidate.x + candidate.width
      && point.y >= candidate.y && point.y <= candidate.y + candidate.height) ?? null;
  }

  async function picked(file: File | undefined) {
    const place = placement.current;
    placement.current = null;
    if (!file || !place) return;
    if (file.size > MAX_BYTES) { setProblem("That file is larger than 20 MB."); return; }
    setBusy(true);
    try {
      const [data, dimensions] = await Promise.all([asDataUrl(file), dimensionsOf(file)]);
      await create({ client_id: place.clientId, name: file.name,
        mime: file.type || "application/octet-stream", data,
        width: dimensions.width, height: dimensions.height,
        canvas_x: place.x, canvas_y: place.y });
      setTool("select"); setProblem("");
    } catch { setProblem("Couldn’t attach that file. Check its format and try again."); }
    finally { setBusy(false); if (input.current) input.current.value = ""; }
  }

  function beginGesture(event: ReactPointerEvent<HTMLElement>, item: BoardImage, mode: Gesture["mode"]) {
    if (event.button !== 0 || tool !== "select") return;
    event.stopPropagation(); event.preventDefault();
    selectObject({ kind: "attachment", id: item.id });
    const article = event.currentTarget.closest(".attachment") as HTMLElement | null;
    article?.focus();
    const box = article?.getBoundingClientRect();
    gesture.current = { pointer: event.pointerId, id: item.id, mode, x: event.clientX, y: event.clientY,
      canvasX: item.canvas_x, canvasY: item.canvas_y, clientId: item.client_id, scale: item.scale,
      boxWidth: box?.width ?? 1, boxHeight: box?.height ?? 1, moved: false };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function moveGesture(event: ReactPointerEvent<HTMLElement>) {
    const active = gesture.current;
    if (!active || active.pointer !== event.pointerId) return;
    const dx = event.clientX - active.x;
    const dy = event.clientY - active.y;
    if (!active.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    active.moved = true;
    setPreview(previewGesture(active, event.clientX, event.clientY));
  }

  function endGesture(event: ReactPointerEvent<HTMLElement>) {
    const active = gesture.current;
    if (!active || active.pointer !== event.pointerId) return;
    gesture.current = null;
    // Derive the final value from the release event. React may not have painted
    // the last pointermove yet, and using the preview state here could otherwise
    // save the penultimate position.
    const final = active.moved ? previewGesture(active, event.clientX, event.clientY) : null;
    setPreview(null);
    if (!active.moved || !final) return;
    let patch: Record<string, unknown>;
    if (active.mode === "move") {
      // Where it ended up, in world coordinates, so the column under it can be
      // found — then stored against whatever that turns out to be. Dragging a
      // file from one column to another moves it *and* re-anchors it, so it
      // travels with its new column from then on.
      const from = regions.find((candidate) => candidate.clientId === active.clientId) ?? null;
      const was = toWorld(from, { x: active.canvasX, y: active.canvasY });
      const world = { x: was.x + final.x, y: was.y + final.y };
      const to = regionAt(world);
      const at = toAnchored(to, world);
      patch = { canvas_x: at.x, canvas_y: at.y, client_id: to?.clientId ?? null };
    } else {
      patch = { scale: final.scale };
    }
    void update(active.id, patch as never, active.mode === "move" ? "Move attachment" : "Resize attachment")
      .catch(() => setProblem(`Couldn’t ${active.mode} that attachment. Try again.`));
  }

  function previewGesture(active: Gesture, clientX: number, clientY: number) {
    const dx = clientX - active.x;
    const dy = clientY - active.y;
    if (active.mode === "move") {
      return { id: active.id, x: dx / transform.scale, y: dy / transform.scale, scale: active.scale };
    }
    const ratio = Math.max((active.boxWidth + dx) / active.boxWidth,
      (active.boxHeight + dy) / active.boxHeight);
    return { id: active.id, x: 0, y: 0,
      scale: Math.max(0.25, Math.min(4, active.scale * ratio)) };
  }

  return <>
    <div className="boardattachments" style={{ transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})` }}>
      {displayed.map(({ item, x, y }) => {
        const isImage = item.mime.startsWith("image/");
        const width = isImage ? Math.min(280, Math.max(120, item.width)) : 230;
        const isSelected = selected?.kind === "attachment" && selected.id === item.id;
        const local = preview?.id === item.id ? preview : null;
        const canDelete = me?.role === "admin" || me?.id === item.uploaded_by;
        return <article className={`attachment${isSelected ? " attachment--selected" : ""}${local ? " attachment--changing" : ""}`}
          key={item.id} tabIndex={tool === "select" ? 0 : -1} role="group"
          aria-label={`${item.original_name} attachment`}
          style={{ left: x + (local?.x ?? 0), top: y + (local?.y ?? 0), width,
            transform: `scale(${local?.scale ?? item.scale})` }}
          onPointerDown={(event) => beginGesture(event, item, "move")}
          onPointerMove={moveGesture} onPointerUp={endGesture}
          onPointerCancel={() => { gesture.current = null; setPreview(null); }}
          onKeyDown={(event) => {
            if ((event.key === "Delete" || event.key === "Backspace") && canDelete) {
              event.preventDefault(); void remove(item.id);
            }
          }}>
          {isImage ? <img src={`/api/attachments/${item.id}/content`} alt={item.original_name} draggable={false} />
            : <span className="attachment__file" aria-hidden="true">{item.original_name.split(".").pop()?.slice(0, 4).toUpperCase() || "FILE"}</span>}
          <span className="attachment__name">{item.original_name}</span>
          <span className="attachment__size">{item.size_bytes < 1024 * 1024
            ? `${Math.max(1, Math.round(item.size_bytes / 1024))} KB`
            : `${(item.size_bytes / 1024 / 1024).toFixed(1)} MB`}</span>
          {isSelected ? <>
            <a className="attachment__open" href={`/api/attachments/${item.id}/content`}
              target="_blank" rel="noreferrer" title="Open attachment"
              onPointerDown={(event) => event.stopPropagation()} aria-label={`Open ${item.original_name}`}>↗</a>
            {/* Same bytes, saved rather than shown. The download attribute only
                works same-origin, which this is — the file is served by the API
                behind the same session cookie as everything else. */}
            <a className="attachment__download" href={`/api/attachments/${item.id}/content`}
              download={item.original_name} title="Download attachment"
              onPointerDown={(event) => event.stopPropagation()}
              aria-label={`Download ${item.original_name}`}>↓</a>
            {canDelete ? <button className="attachment__remove" aria-label={`Remove ${item.original_name}`}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={(event) => { event.stopPropagation(); void remove(item.id); }}>×</button> : null}
            <button className="attachment__resize" aria-label={`Resize ${item.original_name}`}
              title="Drag to resize" onPointerDown={(event) => beginGesture(event, item, "resize")}
              onPointerMove={moveGesture} onPointerUp={endGesture}
              onPointerCancel={() => { gesture.current = null; setPreview(null); }} />
          </> : null}
        </article>;
      })}
    </div>
    <input ref={input} className="visually-hidden" type="file" accept={ACCEPT}
      tabIndex={-1} aria-hidden="true" onChange={(event) => void picked(event.target.files?.[0])} />
    {busy ? <p className="toolproblem" role="status">Attaching…</p> : null}
    {problem ? <p className="toolproblem" role="alert">{problem}</p> : null}
  </>;
}
